import { initializeAnalytics } from '@firebase/analytics';
import { AnalyticsBrowser } from '@segment/analytics-next';
import type { AnalyticsCommonProps, BalanceProps, PurchaseSource, TrackEvent } from '@sunflower-games/analytics-events';
import { logEvent } from 'firebase/analytics';

import type { Wallet } from '../../dto/wallet.types';
import type { LoyaltyTierKey } from '../../stores/loyalty/types';
import type { EnumPaymentMethods } from '../../stores/payredeem/types';
import { getSecondsSinceAppMount, getSecondsSinceSessionStart } from '../../utils/appMount';
import { devError, devLog } from '../../utils/log';
import { getCookie } from '../../utils/secureCookies';
import { postRequest } from '../api';
import type { Currency } from '../auth';
import { logToNewRelic } from '../debug';
import { firebase, firebaseAuth } from '../firebase';
import { getMediaForceTransactionId, sendGoogleTagFtdEvent, sendGoogleTagSuccessPayment } from '../googleTagEvent';
import { resolveFlag } from '../launchDarkly';
import { platformContextService } from '../platformContext/PlatformContextService';

import { getBrowserSessionId } from './getBrowserSessionId';
import { getTabId } from './getTabId';
import { getDevice, getScreenSize } from './helpers';
import { mapMethodToAnalytics } from './mapMethodToAnalytics';
import type { AnalyticsExtendedCommonProps } from './types';

const SEGMENT_APP_KEY = import.meta.env.VITE_SEGMENT_APP_KEY;

class Analytics {
  private service = initializeAnalytics(firebase, {
    config: {
      send_page_view: false,
      // https://developers.google.com/search/blog/2020/01/get-ready-for-new-samesitenone-secure?hl=en
      cookie_flags: 'max-age=7200;secure;samesite=none',
    },
  });

  private segmentAnalytics = new AnalyticsBrowser();

  private isIdentified = false;

  public init() {
    try {
      if (SEGMENT_APP_KEY) {
        this.setCommonProps({
          facebook_fbc: getCookie('_fbc'),
          facebook_fbp: getCookie('_fbp'),
        });
        void this.segmentAnalytics.load(
          {
            writeKey: SEGMENT_APP_KEY,
          },
          {
            highEntropyValuesClientHints: [
              'architecture',
              'bitness',
              'model',
              'platformVersion',
              'uaFullVersion',
              'fullVersionList',
              'wow64',
            ],
          },
        );
        getBrowserSessionId();
      } else {
        logToNewRelic('Segment.init', 'Segment app key is not set', 'error');
      }
    } catch (e) {
      logToNewRelic('Segment.init', 'Failed to initialize segment', 'error');
    }
  }

  public async identify(firebaseId: string) {
    if (!this.isIdentified) {
      try {
        await this.segmentAnalytics.identify(firebaseId, null, { app: { version: __APP_VERSION__ } });
        this.isIdentified = true;
      } catch (error) {
        logToNewRelic('Segment.identify', 'Failed to identify user', 'error');
      }
    }
  }

  public async logout() {
    if (this.isIdentified) {
      try {
        await this.segmentAnalytics.reset();
        this.isIdentified = false;
      } catch (error) {
        logToNewRelic('Segment.logout', 'Failed to logout user', 'error');
      }
    }
  }

  private commonProps: Partial<AnalyticsCommonProps> = {
    lobby_type: 'CC',
  };

  public setCommonProps(props: Partial<AnalyticsCommonProps>) {
    this.commonProps = {
      ...this.commonProps,
      ...props,
    };
  }

  private balanceProps: BalanceProps = {
    social_cc: 0,
    coins_usc: 0,
    coins_bsc: 0,
    coins_rsc: 0,
  };

  public setBalanceProps(props: Wallet) {
    this.balanceProps = {
      social_cc: props.gcCoin,
      coins_rsc: props.scCoin.wsc,
      coins_bsc: props.scCoin.bsc,
      coins_usc: props.scCoin.psc,
    };
  }

  public formatCommonProps(): Partial<AnalyticsExtendedCommonProps> {
    return {
      ...this.commonProps,
      referrer: document.referrer,
      device: getDevice(),
      platform: platformContextService.getConfig().platform,
      screen_size: getScreenSize(),
      user_id: firebaseAuth.currentUser?.uid,
      browser_session_id: getBrowserSessionId(),
      tab_session_id: getTabId(),
      session_time_secs: getSecondsSinceSessionStart(),
      since_app_mount_secs: getSecondsSinceAppMount(),
      event_timestamp: new Date().toISOString(),
    };
  }

  public sendEvent: TrackEvent = async (eventName, paramsArg) => {
    const params = {
      ...this.formatCommonProps(),
      ...this.balanceProps,
      ...paramsArg,
    };
    devLog(
      'sendEvent',
      `Event: ${eventName}${params.step_name ? `, Step: ${params.step_name}` : ''}${params.event_action ? `, Action: ${params.event_action}` : ''}`,
      params,
    );
    logEvent<string>(this.service, String(eventName), params);
    await this.segmentAnalytics.track(String(eventName), params);
  };

  public sendFailedPurchaseEvent(data?: any) {
    void this.sendEvent('purchase', {
      ...data,
      status: 0,
    });
  }

  public sendSuccessPurchaseEvent(data: {
    currency: Currency;
    amount: number;
    packageId: number;
    source: PurchaseSource;
    store_key: string;
    sign_up_date: string;
    ftd: boolean;
    isPvpPurchase: boolean;
    eventParams: {
      transaction_id: string | number;
      payment_method: EnumPaymentMethods | null;
      social_cc_package: number;
      coins_usc_package: number;
      store_segment: LoyaltyTierKey;
    };
    gtagParams: Record<string, any>;
  }) {
    try {
      const { purchase: purchaseFF, ftd: ftdFF } = resolveFlag('purchase_event_debug', {
        purchase: 'both',
        ftd: 'both',
      });

      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event start',
          data,
        },
        'info',
      );

      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'google_tag') {
        // google tag
        sendGoogleTagSuccessPayment({
          ...data.gtagParams,
          ftd: data.ftd,
          value: data.amount,
          currency: data.currency,
          isPvpPurchase: data.isPvpPurchase,
          sign_up_date: data.sign_up_date,
        });
      }
      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'firebase') {
        // firebase analytics
        void this.sendEvent('purchase', {
          step_name: 'purchase',
          is_succeeded: 1,
          payment_method: mapMethodToAnalytics(data.eventParams.payment_method),
          package_id: data.packageId,
          transaction_id: data.eventParams.transaction_id,
          provider: 'unknown',
          store_segment: data.eventParams.store_segment,
          amount: data.amount,
          is_ftd: data.ftd ? 1 : 0,
          // GA4 requirements
          currency: data.currency,
          value: data.amount,
          source: data.source,
          store_key: data.store_key,
          sign_up_date: data.sign_up_date,
        });
      }

      if (data.ftd) {
        if (!ftdFF || ftdFF === 'both' || ftdFF === 'google_tag') {
          sendGoogleTagFtdEvent({
            ...data.gtagParams,
            ftd: data.ftd,
            value: data.amount,
            currency: data.currency,
          });
        }

        const mediaForceTransactionId = getMediaForceTransactionId();
        if (mediaForceTransactionId) {
          this.sendMediaForceEvent('ftd', {
            transactionId: mediaForceTransactionId,
          });
        }
      }

      logToNewRelic('sendSuccessPurchaseEvent', { message: 'event end' }, 'info', { isFtd: data.ftd });
    } catch (e) {
      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event fail',
          data: JSON.stringify(e, null, 2),
        },
        'error',
      );
      devError('sendSuccessPurchaseEvent error', e);
    }
  }

  sendMediaForceEvent(eventName: 'sign_up' | 'ftd', eventData: Record<'transactionId', string>) {
    try {
      void postRequest('/v2/user/mediaForceEvent', {
        eventName,
        eventData: {
          transactionId: eventData.transactionId,
        },
      });
    } catch (e) {
      logToNewRelic('sendMediaForceEvent', 'MediaForce event failed to send', 'debug');
    }
  }

  getSegmentAnonymousId() {
    return this.segmentAnalytics.instance?.user().anonymousId();
  }
}

export const analytics = new Analytics();
